import React, { useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Modal, Table, Alert, message, InputNumber, Row, Col, Card, Empty, Button, Checkbox, Tag, Space, Input, Select, Radio, Spin } from 'antd'
import { RightOutlined, DeleteOutlined, SearchOutlined, DisconnectOutlined } from '@ant-design/icons'
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags'
import { currencyDecimalPlaces, toMinorUnits } from '@utils/currency'
import { useTranslation } from 'react-i18next'
import ShippingTypeSelectImpl from '@shared/selects/ShippingTypeSelect'

interface FulfillmentItem {
  id: string
  order_id?: string
  status?: string
  quantity?: number
  batch?: string
  expiration_date?: string
  custom_attributes?: Record<string, unknown> | null
  product?: { sku?: string; name?: string; [key: string]: unknown }
  unknown_product?: { sku?: string; name?: string; [key: string]: unknown }
  [key: string]: unknown
}

interface SiblingOrder {
  id: string
  name?: string
  [key: string]: unknown
}

interface Warehouse {
  id?: string
  name?: string
  [key: string]: unknown
}

interface ShippingType {
  id?: string
  code?: string
  name?: string
  [key: string]: unknown
}

interface Fulfillment {
  id: string
  items?: FulfillmentItem[]
  warehouse?: Warehouse
  total_amount?: string | number
  currency?: string
  // 接受 null（BE payload 在 fulfillment.shipping_type_id IS NULL 時回傳 null，不是 undefined）
  shipping_type?: ShippingType | null
  merchant_id?: string
  is_cod?: boolean
  [key: string]: unknown
}

export interface PreviewResult {
  success?: boolean
  data?: {
    target_amount?: string | number
    source_amount?: string | number
    currency?: string
    [key: string]: unknown
  }
  message?: string
  [key: string]: unknown
}

export interface SplitItemPayload {
  id: string
  quantity?: number
}

interface FulfillmentSplitModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: (
    itemsPayload: SplitItemPayload[],
    warehouseId: string | null,
    shippingTypeId: string | null,
    desiredAmounts: [string, string]
  ) => void
  fulfillment: Fulfillment | null | undefined
  loading?: boolean
  warehouses?: Warehouse[]
  currentOrderId?: string | null
  siblingOrders?: SiblingOrder[]
  onPreviewAmounts?: ((fulfillmentId: string, itemsPayload: SplitItemPayload[]) => Promise<PreviewResult>) | null
}

const FulfillmentSplitModal: React.FC<FulfillmentSplitModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  fulfillment,
  loading,
  warehouses = [],
  currentOrderId = null,
  siblingOrders = [],
  onPreviewAmounts = null  // (fulfillmentId, itemsPayload) => Promise<{success, data: { target_amount, source_amount, currency }}>
}) => {
  // ShippingTypeSelect 仍是 .jsx，TS 從其 destructure 推導出的 props 不完整，
  // 用 ComponentType<any> 包一層，保留原本 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const ShippingTypeSelect = ShippingTypeSelectImpl as React.ComponentType<any>

  const { t } = useTranslation()
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  // 三種狀態語意：
  //   undefined = 從未動過（render fallback 到 maxQty，視為「全部拆走」）
  //   null      = 使用者明確清空（InputNumber 顯示空欄、payload 退回 record.quantity）
  //   number    = 使用者輸入的明確值
  // 用 null vs undefined 區分「未設定」與「清空」是這份 modal 的核心狀態合約 — 修這條
  // 之前請確認 233 / 347 / 467 三個 callsite 仍走 `!= null` 比較，否則清空會 leak 進 payload。
  const [splitQuantities, setSplitQuantities] = useState<Record<string, number | null | undefined>>({})
  const [searchText, setSearchText] = useState('')
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null)
  const [shippingTypeMode, setShippingTypeMode] = useState<'inherit' | 'custom'>('inherit')
  const [customShippingTypeId, setCustomShippingTypeId] = useState<string | null>(null)
  // 金額：兩格各自獨立編輯，submit 時 validate Σ == fulfillment.total_amount
  // null 表示尚未從 backend preview 取得初值；取得後寫入兩格，使用者可任意調整
  const [targetAmount, setTargetAmount] = useState<number | null>(null)
  const [sourceAmount, setSourceAmount] = useState<number | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  // preview 是否曾失敗（網路 / 後端例外 / admin 端無 splitPreview）— 用來擋下 silent fallback：
  // null = 還沒跑過；false = 上次成功；true = 上次失敗。失敗時必須擋 Confirm，否則使用者會送出
  // desired_amounts=null 觸發後端 AmountSplitter 自動分配（與 FE 顯示金額不一致）。
  const [previewFailed, setPreviewFailed] = useState(false)
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 只有 merchant 有多個 warehouse 時才顯示選擇器
  const showWarehouseSelect = warehouses.length > 1

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedRowKeys([])
      setSplitQuantities({})
      setSearchText('')
      setSelectedWarehouseId(fulfillment?.warehouse?.id || null)
      setShippingTypeMode('inherit')
      setCustomShippingTypeId(null)
      setTargetAmount(null)
      setSourceAmount(null)
      setPreviewFailed(false)
    }
  }, [visible])

  const handleQuantityChange = (id: string, value: number | null) => {
    // 不要把 null 折成 undefined — 否則 render 的「未設定 → fallback maxQty」分支會
    // 立即把清空後的欄位刷回原值，使用者無法清空。
    setSplitQuantities(prev => ({
      ...prev,
      [id]: value
    }))
  }

  const handleRemoveItem = (id: string) => {
    setSelectedRowKeys(prev => prev.filter(key => key !== id))
    setSplitQuantities(prev => {
      const newState = { ...prev }
      delete newState[id]
      return newState
    })
  }

  const handleSelectOutOfStock = (checked: boolean) => {
    if (checked) {
      const oosItems = (fulfillment?.items || []).filter(item => item.status === 'out_of_stock')
      const oosKeys = oosItems.map(item => item.id)

      const newKeys = Array.from(new Set([...selectedRowKeys, ...oosKeys]))
      setSelectedRowKeys(newKeys)

      const newQuantities = { ...splitQuantities }
      oosItems.forEach(item => {
        if (newQuantities[item.id] === undefined) {
          newQuantities[item.id] = item.quantity
        }
      })
      setSplitQuantities(newQuantities)
    }
  }

  const getItemStatusTag = (status?: string): ReactNode => {
    const statusConfig: Record<string, { color: string }> = {
      pending: { color: 'default' },
      allocated: { color: 'success' },
      partial_allocated: { color: 'warning' },
      out_of_stock: { color: 'error' },
      non_inventoried: { color: 'blue' },
      canceled: { color: 'default' },
    }
    const config = (status && statusConfig[status]) || { color: 'default' }
    return (
      <Tag color={config.color} style={{ fontSize: '10px', lineHeight: '1.5', margin: 0 }}>
        {t(`enums.fulfillment_item.status.${status}`, { defaultValue: status })}
      </Tag>
    )
  }

  const renderAttributes = (record: FulfillmentItem): ReactNode => {
    const parts: string[] = []
    if (record.batch) parts.push(`${t('models.inventory.batch')}: ${record.batch}`)
    if (record.expiration_date) parts.push(`${t('models.inventory.expiration_date')}: ${record.expiration_date}`)

    return (
      <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
        <Space direction="vertical" size={0}>
          {parts.map((p, idx) => <span key={idx}>{p}</span>)}
          <CustomAttributesTags attributes={record.custom_attributes} emptyFallback={null} />
        </Space>
      </div>
    )
  }

  // Columns for the Left Side (Source)
  const sourceColumns = [
    {
      title: t('models.fulfillment_item.product'),
      key: 'product',
      ellipsis: true,
      render: (_: unknown, record: FulfillmentItem) => {
        const sku = record.product?.sku || record.unknown_product?.sku || '-'
        const name = record.product?.name || record.unknown_product?.name || ''
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Space align="center" size={4}>
              <span style={{ fontWeight: 500 }}>{sku}</span>
              {getItemStatusTag(record.status)}
            </Space>
            <span style={{ fontSize: '12px', color: '#666' }} title={name}>{name}</span>
            {renderAttributes(record)}
          </div>
        )
      }
    },
    {
      title: t('models.fulfillment_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right' as const,
      render: (qty: number, record: FulfillmentItem) => {
        const isSelected = selectedRowKeys.includes(record.id)
        if (!isSelected) return qty

        // 顯示語意：
        //   undefined = 未動過 → 預設拆 maxQty → remaining = 0
        //   null      = 使用者清空 → 視為拆 0 → remaining = qty（讓使用者一眼看到「現在沒拆任何東西」）
        //   number    = 使用者輸入的明確值
        const stateVal = splitQuantities[record.id]
        const splitQty = stateVal === undefined ? qty : (stateVal ?? 0)
        const remaining = qty - splitQty

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.2' }}>
            <span style={{ color: '#bfbfbf', textDecoration: 'line-through', fontSize: '12px' }}>{qty}</span>
            <span style={{ color: remaining === 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
              {remaining}
            </span>
          </div>
        )
      }
    }
  ]

  // Columns for the Right Side (Target)
  const targetColumns = [
    {
      title: t('models.fulfillment_item.product'),
      key: 'product',
      ellipsis: true,
      render: (_: unknown, record: FulfillmentItem) => {
        const sku = record.product?.sku || record.unknown_product?.sku || '-'
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{sku}</div>
            {renderAttributes(record)}
          </div>
        )
      }
    },
    {
      title: t('shared.orders.detail.fulfillments.split.quantity_col'),
      key: 'split_quantity',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, record: FulfillmentItem) => {
        const maxQty = record.quantity
        // 只在「從未設定」(undefined) 時 fallback 到 maxQty；
        // 使用者清空（null）保留為 null → InputNumber 顯示空欄（不再被刷回 maxQty）。
        const stateVal = splitQuantities[record.id]
        const currentQty = stateVal === undefined ? maxQty : stateVal

        return (
          <InputNumber
            min={1}
            max={maxQty}
            value={currentQty}
            onChange={(val) => handleQuantityChange(record.id, val as number | null)}
            // preview 統一由下方 splitQuantities useEffect (350ms debounce) 觸發 — 不在這裡 immediate fire。
            // 之前同時掛 onBlur immediate + useEffect debounce 會造成「閃兩下」：
            //   user 鍵入 0 → onBlur 立即打 preview(qty=0) → UI 顯示「0/3000」（第一閃）→
            //   antd clamp 0→1 → onChange(1) → state 變 → useEffect 350ms 後打 preview(qty=1) → 「300/2700」（第二閃）
            // 改成只用 debounce 後，clamp 完成 → 350ms idle → 一次性 preview，UI 只更新一次。
            // Enter 仍保留 immediate fire — 使用者主動 commit 的 gesture，期待即時回饋。
            onPressEnter={() => runPreviewRef.current?.()}
            size="small"
            style={{ width: '100%' }}
          />
        )
      }
    },
    {
      title: '',
      key: 'action',
      width: 40,
      align: 'center' as const,
      render: (_: unknown, record: FulfillmentItem) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.id)}
        />
      )
    }
  ]

  // Calculate Data Sources
  const allItems: FulfillmentItem[] = fulfillment?.items || []
  const targetItems = allItems.filter(item => selectedRowKeys.includes(item.id))

  // ── 金額顯示 ──
  // Preview 由 backend split_preview API 提供（共用 AmountSplitter 邏輯，無 FE/BE drift）
  // 兩格各自獨立編輯；submit 時驗證 Σ == fulfillment.total_amount，不符擋下不送。
  const fulfillmentTotal = parseFloat(String(fulfillment?.total_amount || 0))
  const fulfillmentCurrency = fulfillment?.currency
  // 依貨幣推 InputNumber 的步進與小數位 — JPY/TWD/KRW 用整數元，USD 等用 cent。
  const amountDecimalPlaces = currencyDecimalPlaces(fulfillmentCurrency)
  const amountStep = amountDecimalPlaces === 0 ? 1 : Math.pow(10, -amountDecimalPlaces)

  // runPreview 用 ref 拿最新閉包，避免 useEffect 依賴 splitQuantities 造成「打字中 fire 多次」
  const runPreviewRef = useRef<(() => Promise<void>) | null>(null)
  runPreviewRef.current = async () => {
    if (!onPreviewAmounts || !fulfillment?.id) {
      // 沒有 preview 入口（例：admin 端尚未實作 splitPreview）— 視同 preview 失敗，
      // 擋下 Confirm 不讓使用者 silent 走到後端 AmountSplitter 自動分配。
      setPreviewFailed(true)
      setTargetAmount(null)
      setSourceAmount(null)
      return
    }
    if (selectedRowKeys.length === 0) {
      setTargetAmount(0)
      setSourceAmount(fulfillmentTotal)
      setPreviewFailed(false)
      return
    }
    // 金額為 0 或無金額時，拆分本來就沒有可分配金額 — 兩邊都 0，沒必要打 backend preview
    if (!Number.isFinite(fulfillmentTotal) || fulfillmentTotal <= 0) {
      setTargetAmount(0)
      setSourceAmount(0)
      setPreviewFailed(false)
      return
    }
    setPreviewLoading(true)
    try {
      const itemsPayload: SplitItemPayload[] = selectedRowKeys.map(id => {
        const record = allItems.find(i => i.id === id)
        const qty = splitQuantities[id]
        // 三種狀態：
        //   undefined = 未動過 → 預設全拆 → 送 record.quantity（歷史合約）
        //   null      = 使用者清空 → 視為 0 → 送 0（與左側「remaining = qty」顯示對齊）
        //   number    = 明確值 → 直接送
        const finalQty = qty === undefined ? record?.quantity : (qty ?? 0)
        return { id, quantity: finalQty }
      })
      const result = await onPreviewAmounts(fulfillment.id, itemsPayload)
      if (result?.success && result.data) {
        // parseFloat 可能回 NaN（後端送 null / 非數字字串）；用 Number.isFinite 守門，
        // 避免 NaN 寫進 state 後 submit 時印「金額加總 (NaN) 與原金額不符」這種無 actionable 的訊息
        const targetVal = parseFloat(String(result.data.target_amount))
        const sourceVal = parseFloat(String(result.data.source_amount))
        if (Number.isFinite(targetVal) && Number.isFinite(sourceVal)) {
          setTargetAmount(targetVal)
          setSourceAmount(sourceVal)
          setPreviewFailed(false)
        } else {
          console.error('[FulfillmentSplitModal] preview returned non-finite amount:', result.data)
          setTargetAmount(null)
          setSourceAmount(null)
          setPreviewFailed(true)
          message.warning(t('shared.orders.detail.fulfillments.split.preview_failed'))
        }
      } else {
        // result.success=false 也算失敗（含 admin 端 'preview not supported' fallback）
        setTargetAmount(null)
        setSourceAmount(null)
        setPreviewFailed(true)
        message.warning(t('shared.orders.detail.fulfillments.split.preview_failed'))
      }
    } catch (err) {
      console.error('[FulfillmentSplitModal] preview failed:', err)
      setTargetAmount(null)
      setSourceAmount(null)
      setPreviewFailed(true)
      message.warning(t('shared.orders.detail.fulfillments.split.preview_failed'))
    } finally {
      setPreviewLoading(false)
    }
  }

  // 勾選/取消 item 或數量改動時 debounce 拉 preview。
  // splitQuantities 加進 deps 是為了 cover antd InputNumber 的「auto-clamp 後 onChange」case：
  //   使用者鍵入 0 → onChange(0) → state=0 → onBlur 立即 fire preview（用 state=0）→
  //   antd 內部 clamp 到 min=1 → 非同步 onChange(1) → state=1，但金額已是用 0 算出的舊結果
  // 把 splitQuantities 加進 deps，350ms debounce 後會用最新（已 clamp）的 state 重打 preview，
  // 打字中也不會 fire 多次（debounce 守住）。
  useEffect(() => {
    if (!visible) return
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)
    previewDebounceRef.current = setTimeout(() => runPreviewRef.current?.(), 350)
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)
    }
  }, [visible, fulfillment?.id, selectedRowKeys, splitQuantities])

  // Filter source items based on search
  const filteredSourceItems = allItems.filter(item => {
    if (!searchText) return true
    const keyword = searchText.toLowerCase()
    const sku = (item.product?.sku || item.unknown_product?.sku || '').toLowerCase()
    const name = (item.product?.name || item.unknown_product?.name || '').toLowerCase()
    return sku.includes(keyword) || name.includes(keyword)
  })

  const oosCount = allItems.filter(item => item.status === 'out_of_stock').length

  // 按訂單分組（用於合併後的快速拆分）
  const orderGroups = useMemo(() => {
    if (!currentOrderId) return []
    const groups: Record<string, FulfillmentItem[]> = {}
    allItems.forEach(item => {
      const oid = item.order_id
      if (!oid || oid === currentOrderId) return
      if (!groups[oid]) groups[oid] = []
      groups[oid].push(item)
    })
    return Object.entries(groups).map(([orderId, items]) => {
      const info = siblingOrders.find(o => o.id === orderId)
      return { orderId, orderName: info?.name || orderId, items }
    })
  }, [allItems, currentOrderId, siblingOrders])

  // 按訂單快速選取（toggle）
  const handleSelectByOrder = (orderId: string) => {
    const orderItems = allItems.filter(i => i.order_id === orderId)
    const orderItemIds = orderItems.map(i => i.id)
    const allSelected = orderItemIds.every(id => selectedRowKeys.includes(id))

    if (allSelected) {
      // 取消選取
      const idSet = new Set(orderItemIds)
      setSelectedRowKeys(prev => prev.filter(key => !idSet.has(key)))
      setSplitQuantities(prev => {
        const newQuantities = { ...prev }
        orderItemIds.forEach(id => delete newQuantities[id])
        return newQuantities
      })
    } else {
      // 選取
      const newKeys = Array.from(new Set([...selectedRowKeys, ...orderItemIds]))
      setSelectedRowKeys(newKeys)
      const newQuantities = { ...splitQuantities }
      orderItems.forEach(item => {
        if (newQuantities[item.id] === undefined) {
          newQuantities[item.id] = item.quantity
        }
      })
      setSplitQuantities(newQuantities)
    }
  }

  const handleOk = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('shared.orders.detail.fulfillments.split.select_items_warning'))
      return
    }

    // Preview 失敗 / 從未成功 → 擋下。否則使用者會看到 FE 顯示舊金額（或空白）卻送出
    // desired_amounts=null 觸發後端 AmountSplitter 自動分配，造成「看到的金額 ≠ 實際分配」。
    if (previewFailed || !Number.isFinite(targetAmount) || !Number.isFinite(sourceAmount)) {
      message.error(t('shared.orders.detail.fulfillments.split.preview_required'))
      return
    }

    const itemsPayload: SplitItemPayload[] = selectedRowKeys.map(id => {
      const record = allItems.find(i => i.id === id)
      const qty = splitQuantities[id]
      // 三種狀態（與 preview 路徑對齊）：
      //   undefined = 未動過 → 預設全拆 → 送 record.quantity
      //   null      = 使用者清空 → 視為 0 → 送 0（BE 端會擋無效 0 拆分；對齊 UI 顯示「拆 0、剩 qty」）
      //   number    = 明確值
      const finalQty = qty === undefined ? record?.quantity : (qty ?? 0)

      return {
        id: id,
        quantity: finalQty
      }
    })

    // Submit-time validation：Σ 必須 == fulfillment.total_amount，否則擋下不送。
    // 用整數 minor-unit 比對（cent / 整數元 / 整數韓元）— Float 加總在邊界值（如 0.1 + 0.2 =
    // 0.30000000000000004）會 false-reject backend BigDecimal 算得通過的請求。
    //
    // 乘數由 currency 推（toMinorUnits 內部走 currencyDecimalPlaces）：
    // - JPY/TWD/KRW (decimal_places=0) → × 1，整數元比對
    // - USD/EUR/SGD/CNY/HKD (decimal_places=2) → × 100，cent 比對
    // 與 backend `AmountSplitter` 的 `currency_decimal_places` 對齊；任何不同步會造成 false-reject。
    const targetMinor = toMinorUnits(targetAmount, fulfillmentCurrency)
    const sourceMinor = toMinorUnits(sourceAmount, fulfillmentCurrency)
    const totalMinor = toMinorUnits(fulfillmentTotal, fulfillmentCurrency)
    if (targetMinor + sourceMinor !== totalMinor) {
      const divisor = Math.pow(10, amountDecimalPlaces)
      message.error(t('shared.orders.detail.fulfillments.split.amount_mismatch', {
        sum: ((targetMinor + sourceMinor) / divisor).toFixed(amountDecimalPlaces),
        total: (totalMinor / divisor).toFixed(amountDecimalPlaces)
      }))
      return
    }

    // 只有選了不同倉庫時才傳 warehouseId
    const warehouseId = selectedWarehouseId !== fulfillment?.warehouse?.id ? selectedWarehouseId : null
    const shippingTypeId = shippingTypeMode === 'custom' ? customShippingTypeId : null
    // 永遠送 desired_amounts（FE 是金額的最終決定者，backend 不再跑 AmountSplitter）。
    // toFixed 位數依貨幣推：JPY/TWD/KRW 送整數 "333"，USD 送 cent "333.33"。
    const desiredAmounts: [string, string] = [ targetAmount!.toFixed(amountDecimalPlaces), sourceAmount!.toFixed(amountDecimalPlaces) ]
    onConfirm(itemsPayload, warehouseId, shippingTypeId, desiredAmounts)
  }

  // 任一邊總數量為 0 都不是有效拆分（沒拆任何東西、或源出貨單被整個拆空）→ disable 送出。
  // 關鍵：source 整張 FL 的剩餘 = 全部 items 量總和 − 已勾選的拆分量；未勾選品項完全留在 source，
  // 不應被當「拆 0、剩 qty」灌進 selected 的局部加總（會誤把「勾一個全拆走、其他未勾留著」判成 source 空）。
  // splitQuantities 語意：undefined=未動過(預設全拆)、null=清空(視為拆 0)、number=明確值。
  const { totalSplit, totalSourceRemaining } = useMemo(() => {
    let split = 0
    selectedRowKeys.forEach((id) => {
      const item = allItems.find(i => i.id === id)
      if (!item) return
      const qty = item.quantity ?? 0
      const stateVal = splitQuantities[id as string]
      const splitQty = stateVal === undefined ? qty : (stateVal ?? 0)
      split += splitQty
    })
    const allItemsTotalQty = allItems.reduce((sum, it) => sum + (it.quantity ?? 0), 0)
    return { totalSplit: split, totalSourceRemaining: allItemsTotalQty - split }
  }, [selectedRowKeys, splitQuantities, allItems])

  const splitDisabled = selectedRowKeys.length === 0 || totalSplit === 0 || totalSourceRemaining === 0

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      const keys = newSelectedRowKeys as string[]
      setSelectedRowKeys(keys)

      const newQuantities = { ...splitQuantities }
      keys.forEach(key => {
        if (newQuantities[key] === undefined) {
          const item = allItems.find(i => i.id === key)
          if (item) newQuantities[key] = item.quantity
        }
      })
      setSplitQuantities(newQuantities)
    },
    preserveSelectedRowKeys: true
  }

  return (
    <Modal
      title={t('shared.orders.detail.fulfillments.split.title')}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okButtonProps={{ disabled: splitDisabled }}
      width={1000}
      centered
    >
      <Alert
        message={t('shared.orders.detail.fulfillments.split.description')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder={t('shared.orders.detail.fulfillments.split.search_placeholder')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        {oosCount > 0 && (
          <Checkbox onChange={(e) => handleSelectOutOfStock(e.target.checked)}>
            {t('shared.orders.detail.fulfillments.split.select_all_oos', { count: oosCount })}
          </Checkbox>
        )}
      </div>

      {orderGroups.length > 0 && (
        <Alert
          message={t('shared.orders.detail.fulfillments.split.unmerge_hint')}
          type="warning"
          showIcon
          icon={<DisconnectOutlined />}
          style={{ marginBottom: 12 }}
          description={
            <Space wrap style={{ marginTop: 8 }}>
              {orderGroups.map(({ orderId, orderName, items }) => {
                const allSelected = items.every(i => selectedRowKeys.includes(i.id))
                return (
                  <Button
                    key={orderId}
                    size="small"
                    type={allSelected ? 'primary' : 'default'}
                    ghost={allSelected}
                    onClick={() => handleSelectByOrder(orderId)}
                  >
                    {orderName} ({items.length} {t('shared.items', { defaultValue: 'items' })})
                  </Button>
                )
              })}
            </Space>
          }
        />
      )}

      <Row gutter={16} style={{ height: '500px' }}>
        {/* Source Column */}
        <Col span={11} style={{ height: '100%' }}>
          <Card
            title={t('shared.orders.detail.fulfillments.split.source_fulfillment')}
            size="small"
            bodyStyle={{ padding: 0, height: '450px', overflowY: 'auto' }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, fontSize: '12px', color: '#666' }}>
              <span>{t('shared.orders.detail.fulfillments.split.amount', { defaultValue: '金額' })}:</span>
              {previewLoading ? <Spin size="small" /> : (
                <InputNumber
                  size="small"
                  min={0}
                  step={amountStep}
                  precision={amountDecimalPlaces}
                  value={sourceAmount}
                  onChange={(val) => setSourceAmount(val as number | null)}
                  style={{ width: 120 }}
                />
              )}
              <span>{fulfillmentCurrency}</span>
            </div>
            <Table
              dataSource={filteredSourceItems}
              columns={sourceColumns}
              rowKey="id"
              pagination={false}
              rowSelection={rowSelection}
              size="small"
              sticky
            />
          </Card>
        </Col>

        {/* Arrow Column */}
        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <RightOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
        </Col>

        {/* Target Column */}
        <Col span={11} style={{ height: '100%' }}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                  <span>{t('shared.orders.detail.fulfillments.split.target_fulfillment')}</span>
                  {showWarehouseSelect && (
                    <Select
                      size="small"
                      value={selectedWarehouseId}
                      onChange={setSelectedWarehouseId}
                      style={{ minWidth: 120, flex: 1 }}
                      options={warehouses.map(w => ({ label: w.name, value: w.id }))}
                    />
                  )}
                </div>
                <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#bfbfbf', flexShrink: 0 }}>
                  {targetItems.length} {t('shared.items', { defaultValue: 'items' })}
                </span>
              </div>
            }
            size="small"
            bodyStyle={{ padding: 0, height: '450px', overflowY: 'auto', backgroundColor: '#f9f9f9' }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px dashed #1890ff' }}
            headStyle={{ backgroundColor: '#e6f7ff', borderBottom: '1px dashed #1890ff' }}
          >
            <div style={{ padding: '8px 12px', borderBottom: '1px dashed #1890ff', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, fontSize: '12px', color: '#666', backgroundColor: '#e6f7ff' }}>
              <span>{t('shared.orders.detail.fulfillments.split.amount', { defaultValue: '金額' })}:</span>
              {previewLoading ? <Spin size="small" /> : (
                <InputNumber
                  size="small"
                  min={0}
                  step={amountStep}
                  precision={amountDecimalPlaces}
                  value={targetAmount}
                  onChange={(val) => setTargetAmount(val as number | null)}
                  style={{ width: 120 }}
                />
              )}
              <span>{fulfillmentCurrency}</span>
            </div>
            {targetItems.length > 0 ? (
              <Table
                dataSource={targetItems}
                columns={targetColumns}
                rowKey="id"
                pagination={false}
                size="small"
                sticky
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('shared.orders.detail.fulfillments.split.select_items_warning')}
                style={{ marginTop: '100px' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 物流類型選擇 */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 6 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          {t('shared.orders.detail.fulfillments.split.new_shipping_type', { defaultValue: '新 Fulfillment 物流類型' })}
        </div>
        <Radio.Group
          value={shippingTypeMode}
          onChange={(e) => {
            setShippingTypeMode(e.target.value)
            if (e.target.value === 'inherit') setCustomShippingTypeId(null)
          }}
        >
          <Space direction="vertical">
            <Radio value="inherit">
              {t('shared.orders.detail.fulfillments.split.inherit_shipping_type', { defaultValue: '繼承原物流' })}
              {fulfillment?.shipping_type && (
                <Tag style={{ marginLeft: 8 }}>
                  {t(`shipping_types.${fulfillment.shipping_type.code}`, { defaultValue: fulfillment.shipping_type.name })}
                </Tag>
              )}
            </Radio>
            <Radio value="custom">
              {t('shared.orders.detail.fulfillments.split.custom_shipping_type', { defaultValue: '指定其他物流' })}
            </Radio>
          </Space>
        </Radio.Group>
        {shippingTypeMode === 'custom' && (
          <div style={{ marginTop: 8, marginLeft: 24 }}>
            <ShippingTypeSelect
              merchantId={fulfillment?.merchant_id}
              codOnly={fulfillment?.is_cod || false}
              currentShippingTypeId={fulfillment?.shipping_type?.id}
              value={customShippingTypeId}
              onChange={setCustomShippingTypeId}
              style={{ width: 300 }}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default FulfillmentSplitModal
